// src/lib/pickGroups.js
//
// Pure helpers for the pick-submission UI, used by PickSubmission (user-facing)
// and AdminPickEntry (admin "enter picks on behalf of" tool).
//
// Lifted verbatim from those two files in PR-A. Behavior unchanged.

// Recursively builds a "... or ..." label for a placeholder game by
// walking source_game1_id / source_game2_id until it finds a game with real
// teams. Used when no participant pick exists to resolve the placeholder.
//
// Earlier versions wrapped each branch as "W(...)" (winner of), but the
// nested chains became unreadable for deep placeholders (Round 5+ would
// show 16 teams with multiple layers of W() wrapping). Dropping the
// wrapper and just joining with "or" reads cleaner. The downside is no
// visual distinction between "real matchup" and "placeholder of upstream
// matchups", but in practice this label is only seen briefly before the
// participant resolves it via getResolvedLabel by making picks.
export function getSourceLabel(gameId, gameMap) {
    const game = gameMap[gameId];
    if (!game) return '?';
    if (game.team1_name) return `${game.team1_name} vs ${game.team2_name}`;
    const left  = getSourceLabel(game.source_game1_id, gameMap);
    const right = getSourceLabel(game.source_game2_id, gameMap);
    return `${left} or ${right}`;
}

// Like getSourceLabel, but uses the participant's own picks to chain through
// previous rounds. If the participant has picked from this source game, show
// the team they picked. Otherwise show the matchup so they can see what
// they're picking between.
//
// Used for displaying the team buttons on placeholder games in Bracket pools
// and later groups of Round-by-Round pools.
export function getResolvedLabel(gameId, gameMap, picks) {
    const game = gameMap[gameId];
    if (!game) return '?';

    const myPick = picks[gameId];

    // First priority: the participant's own pick on this source game.
    // We want "Akron" not "Akron vs Drake" once they've committed.
    if (myPick) {
        if (myPick.picked_team_id) {
            if (game.team1_id === myPick.picked_team_id) return game.team1_name;
            if (game.team2_id === myPick.picked_team_id) return game.team2_name;
        }
        // Placeholder pick (they picked a source game that itself was a placeholder
        // at the time). Recurse to resolve.
        if (myPick.source_game_pick_id) {
            return getResolvedLabel(myPick.source_game_pick_id, gameMap, picks);
        }
    }

    // No pick yet. If the source game has real teams, show the matchup so the
    // participant can see what they need to pick between.
    if (game.team1_name && game.team2_name) {
        return `${game.team1_name} vs ${game.team2_name}`;
    }

    // No pick, no real teams — it's a placeholder of a placeholder. Recurse
    // generically to get a "W(... vs ...)" label.
    return getSourceLabel(gameId, gameMap);
}

// Returns the effective tournament_teams.id that this participant's pick
// represents on the given game. Mirrors the backend scoring engine's
// "prefer picked_team_id, fall back to walking source_game_pick_id" logic
// (see PUT /games/:id/result in routes/games.js).
//
// Why this exists: when a Round 2+ game is FIRST picked, it's a placeholder
// (team1_id and team2_id are NULL on games), and the handler stores the
// pick as source_game_pick_id with picked_team_id=NULL. Once the upstream
// game(s) finalize and the placeholder game's team1_id/team2_id are
// populated, the pick row keeps its original placeholder shape — but the
// PickCard renderer needs to know which of the now-real teams to highlight.
// This helper walks the source-game chain to the participant's earliest
// concrete picked_team_id and returns it.
//
// Returns null if the chain breaks or no concrete pick is found within
// maxDepth hops. maxDepth=10 matches the backend safety bound; bracket
// depth is at most 6.
export function resolvePickedTeamId(myPick, picks) {
    if (!myPick) return null;
    if (myPick.picked_team_id) return myPick.picked_team_id;

    let cursor = myPick.source_game_pick_id;
    let depth  = 0;
    const maxDepth = 10;

    while (cursor && depth < maxDepth) {
        const upstream = picks[cursor];
        if (!upstream) return null;             // chain broken
        if (upstream.picked_team_id) return upstream.picked_team_id;
        cursor = upstream.source_game_pick_id;
        depth++;
    }
    return null;
}

// Builds the "round group" structure used by both the user-facing and admin
// pick UIs. Bracket pools have a single "All Rounds" group. Round-by-Round
// pools may pair rounds together (e.g. "Rounds 1 & 2") via the pick_group
// column on rounds.
export function buildGroups(rounds, poolType) {
    if (poolType === 'bracket') {
        return [{
            key:        'all',
            label:      'All Rounds',
            rounds:     rounds,
            pick_group: null
        }];
    }
    const groups  = [];
    const grouped = {};
    for (const r of rounds) {
        if (r.pick_group) {
            if (!grouped[r.pick_group]) {
                grouped[r.pick_group] = {
                    key:        `group-${r.pick_group}`,
                    label:      `Rounds ${r.round_number}`,
                    rounds:     [],
                    pick_group: r.pick_group
                };
                groups.push(grouped[r.pick_group]);
            } else {
                grouped[r.pick_group].label += ` & ${r.round_number}`;
            }
            grouped[r.pick_group].rounds.push(r);
        } else {
            groups.push({
                key:        `round-${r.round_number}`,
                label:      r.round_name,
                rounds:     [r],
                pick_group: null
            });
        }
    }
    return groups;
}